<script setup lang="ts">
/**
 * Check your email (Figma `66:47106`).
 *
 * Reached after every reset request, registered address or not — the copy is
 * phrased conditionally ("If an account exists for …") precisely so the screen
 * carries no information about who has an account (`66:47515`).
 *
 * "Resend link" is intentionally tonal rather than primary: the user's next
 * step is in their inbox, not here.
 */
import { FairButton } from '@/ui/primitives/FairButton'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthModal, useUserStore } from '@/features/auth/store'
import { requestPasswordReset } from '@/features/auth/api/password'
import { mapAuthError } from '@/features/auth/model/mapAuthError'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import { useFairToast } from '@/ui/patterns/FairToast'
import illustrationUrl from '@/assets/img/auth/illustration-check-email.png'

const { t } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()
const userStore = useUserStore()

const resending = ref(false)
const address = computed(() => userStore.tempEmailForPasswordReset ?? '')

async function handleResend() {
  if (resending.value || !address.value) return
  resending.value = true
  try {
    await requestPasswordReset(address.value)
    toast.show({ message: t('auth.toast.reset_link_sent'), tone: 'success' })
  } catch (error) {
    const mapped = mapAuthError(error, 'forgotPassword')
    if (mapped.status === 429) {
      toast.show({ message: t('auth.errors.too_many_requests'), tone: 'warning' })
      return
    }
    // Same reasoning as the request screen: a 4xx must not become a signal
    // about the address, so only real failures are reported.
    if (mapped.status === undefined || mapped.status >= 500) {
      toast.show({ message: t('auth.errors.generic'), tone: 'warning' })
      return
    }
    toast.show({ message: t('auth.toast.reset_link_sent'), tone: 'success' })
  } finally {
    resending.value = false
  }
}
</script>

<template>
  <AuthScreenShell
    centered
    :title="t('auth.check_email.title')"
  >
    <template #art>
      <img
        class="fair-auth-screen__art"
        :src="illustrationUrl"
        alt=""
      >
    </template>

    <template #description>
      {{ t('auth.check_email.description', { email: address }) }}
    </template>

    <FairButton
      class="fair-auth-cta"
      size="auto"
      variant="tonal"
      :label="t('auth.check_email.resend')"
      :disabled="resending || !address"
      @click="handleResend"
    />

    <p class="fair-auth-switch">
      <button
        type="button"
        class="fair-auth-form__link"
        @click="authModal.showAuthModal('login')"
      >
        {{ t('auth.check_email.back_to_login') }}
      </button>
    </p>
  </AuthScreenShell>
</template>
